/**
 * Template engine for dynamic mock response generation.
 *
 * Supports three categories of template variables:
 *
 * 1. Built-in generators ($ prefix): {{$uuid}}, {{$randomInt}}, {{$timestamp}}, etc.
 * 2. Request context: {{request.method}}, {{request.header.Name}}, {{request.query.key}}, {{request.body.field}}
 * 3. Path parameters (backward compatible): {{paramName}}
 */

// --- Types ---

import type { ParsedBody } from '../types/parsed-body';

export interface TemplateContext {
  pathParams: Record<string, string>;
  request: {
    method: string;
    path: string;
    headers: Record<string, string>;
    query: Record<string, string>;
    body: string | null;
    parsedBody?: ParsedBody;
  };
  variables?: Record<string, unknown>;
  project?: Record<string, unknown>;
  globals?: Record<string, unknown>;
}

// --- Random data pools ---

const FIRST_NAMES = [
  'Alice', 'Bob', 'Carol', 'Dave', 'Eve', 'Frank', 'Grace', 'Heidi',
  'Ivan', 'Judy', 'Karl', 'Laura', 'Mallory', 'Nina', 'Oscar', 'Peggy',
  'Quinn', 'Rupert', 'Sybil', 'Trent', 'Ursula', 'Victor', 'Wendy', 'Xander',
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Wilson', 'Anderson', 'Taylor', 'Thomas',
];

const EMAIL_DOMAINS = [
  'example.com', 'test.com', 'mock.dev', 'sample.org', 'demo.net',
];

const STREETS = [
  'Main St', 'Oak Ave', 'Maple Dr', 'Cedar Ln', 'Elm St', 'Park Ave',
  'Washington Blvd', 'Lake Rd', 'Hill St', 'River Rd', 'Pine St', 'Sunset Blvd',
];

const CITIES = [
  'Portland', 'Austin', 'Denver', 'Seattle', 'Chicago', 'Boston',
  'Miami', 'Phoenix', 'Atlanta', 'Nashville', 'San Diego', 'Minneapolis',
];

const STATES = [
  'Alabama', 'Alaska', 'Arizona', 'California', 'Colorado', 'Connecticut',
  'Florida', 'Georgia', 'Illinois', 'Massachusetts', 'Michigan', 'Minnesota',
  'New York', 'Oregon', 'Pennsylvania', 'Texas', 'Virginia', 'Washington',
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Germany', 'France',
  'Australia', 'Japan', 'Brazil', 'India', 'Mexico', 'South Korea', 'Italy',
];

const WORDS = [
  'synergy', 'optimize', 'leverage', 'streamline', 'innovate', 'scalable',
  'robust', 'seamless', 'agile', 'dynamic', 'efficient', 'integrate',
  'deploy', 'iterate', 'transform', 'accelerate', 'empower', 'disrupt',
];

const SENTENCES = [
  'The quick service responds promptly to all requests.',
  'Data flows seamlessly between integrated components.',
  'Users experience fast and reliable performance.',
  'The system scales efficiently under heavy load.',
  'Automated processes handle routine operations smoothly.',
  'Real-time updates keep all clients synchronized.',
  'Secure connections protect sensitive information.',
  'Analytics provide actionable insights for teams.',
];

const USER_AGENTS = [
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Safari/605.1.15',
  'Mozilla/5.0 (X11; Linux x86_64; rv:121.0) Gecko/20100101 Firefox/121.0',
  'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1',
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Edge/120.0.0.0 Safari/537.36',
];

const ALPHANUMERIC = 'abcdefghijklmnopqrstuvwxyz0123456789';

// --- Helpers ---

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomElement<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomUUID(): string {
  const hex = '0123456789abcdef';
  const sections = [8, 4, 4, 4, 12];
  return sections
    .map((len) => {
      let s = '';
      for (let i = 0; i < len; i++) {
        s += hex[Math.floor(Math.random() * 16)];
      }
      return s;
    })
    .join('-');
}

function randomString(length: number): string {
  let s = '';
  for (let i = 0; i < length; i++) {
    s += ALPHANUMERIC[Math.floor(Math.random() * ALPHANUMERIC.length)];
  }
  return s;
}

function randomEmail(): string {
  const first = randomElement(FIRST_NAMES).toLowerCase();
  const last = randomElement(LAST_NAMES).toLowerCase();
  const domain = randomElement(EMAIL_DOMAINS);
  return `${first}.${last}@${domain}`;
}

function randomName(): string {
  return `${randomElement(FIRST_NAMES)} ${randomElement(LAST_NAMES)}`;
}

function randomPhone(): string {
  return `(555) ${randomInt(100, 999)}-${randomInt(1000, 9999)}`;
}

function randomUsername(): string {
  const first = randomElement(FIRST_NAMES).toLowerCase();
  const last = randomElement(LAST_NAMES).toLowerCase();
  return `${first}.${last}${randomInt(1, 99)}`;
}

function randomIPv4(): string {
  return `${randomInt(1, 255)}.${randomInt(0, 255)}.${randomInt(0, 255)}.${randomInt(1, 254)}`;
}

function randomIPv6(): string {
  const hex = '0123456789abcdef';
  const groups: string[] = [];
  for (let g = 0; g < 8; g++) {
    let group = '';
    for (let i = 0; i < 4; i++) {
      group += hex[Math.floor(Math.random() * 16)];
    }
    groups.push(group);
  }
  return groups.join(':');
}

function randomColor(): string {
  const hex = '0123456789abcdef';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += hex[Math.floor(Math.random() * 16)];
  }
  return color;
}

function randomDateOffset(daysMin: number, daysMax: number): Date {
  const now = Date.now();
  const offsetMs = randomInt(daysMin, daysMax) * 86400000;
  return new Date(now + offsetMs);
}

function randomSlug(): string {
  const parts: string[] = [];
  for (let i = 0; i < 3; i++) {
    parts.push(randomElement(WORDS));
  }
  return parts.join('-');
}

function randomParagraph(): string {
  const count = randomInt(2, 4);
  const picked: string[] = [];
  for (let i = 0; i < count; i++) {
    picked.push(randomElement(SENTENCES));
  }
  return picked.join(' ');
}

// --- Built-in variable resolvers ---

type BuiltinResolver = (args?: string[]) => string;

const BUILTIN_VARIABLES: Record<string, BuiltinResolver> = {
  // Original generators
  '$uuid': () => randomUUID(),
  '$randomInt': (args) => {
    const min = args?.[0] !== undefined ? parseInt(args[0], 10) : 0;
    const max = args?.[1] !== undefined ? parseInt(args[1], 10) : 1000;
    return String(randomInt(min, max));
  },
  '$randomFloat': (args) => {
    const min = args?.[0] !== undefined ? parseFloat(args[0]) : 0;
    const max = args?.[1] !== undefined ? parseFloat(args[1]) : 1;
    const decimals = args?.[2] !== undefined ? parseInt(args[2], 10) : 2;
    return (min + Math.random() * (max - min)).toFixed(decimals);
  },
  '$randomBool': () => (Math.random() < 0.5 ? 'true' : 'false'),
  '$timestamp': () => new Date().toISOString(),
  '$timestampUnix': () => String(Math.floor(Date.now() / 1000)),
  '$date': () => new Date().toISOString().split('T')[0],
  '$randomEmail': () => randomEmail(),
  '$randomName': () => randomName(),
  '$randomString': (args) => {
    const len = args?.[0] !== undefined ? parseInt(args[0], 10) : 16;
    return randomString(len);
  },

  // Identity & Contact
  '$firstName': () => randomElement(FIRST_NAMES),
  '$lastName': () => randomElement(LAST_NAMES),
  '$phone': () => randomPhone(),
  '$username': () => randomUsername(),
  '$avatar': () => `https://i.pravatar.cc/150?u=${randomString(8)}`,

  // Location
  '$street': () => `${randomInt(100, 9999)} ${randomElement(STREETS)}`,
  '$city': () => randomElement(CITIES),
  '$state': () => randomElement(STATES),
  '$zip': () => String(randomInt(10000, 99999)),
  '$country': () => randomElement(COUNTRIES),
  '$latitude': () => (Math.random() * 180 - 90).toFixed(4),
  '$longitude': () => (Math.random() * 360 - 180).toFixed(4),

  // Text
  '$word': () => randomElement(WORDS),
  '$sentence': () => randomElement(SENTENCES),
  '$paragraph': () => randomParagraph(),
  '$slug': () => randomSlug(),

  // Date/Time
  '$dateISO': () => new Date().toISOString(),
  '$datePast': () => randomDateOffset(-365, -1).toISOString(),
  '$dateFuture': () => randomDateOffset(1, 365).toISOString(),
  '$time': () => `${String(randomInt(0, 23)).padStart(2, '0')}:${String(randomInt(0, 59)).padStart(2, '0')}:${String(randomInt(0, 59)).padStart(2, '0')}`,

  // Internet
  '$url': () => `https://${randomElement(EMAIL_DOMAINS)}/${randomSlug()}`,
  '$domain': () => randomElement(EMAIL_DOMAINS),
  '$ipv4': () => randomIPv4(),
  '$ipv6': () => randomIPv6(),
  '$color': () => randomColor(),
  '$userAgent': () => randomElement(USER_AGENTS),

  // Parameterized
  '$oneOf': (args) => {
    if (!args || args.length === 0) return '';
    // Args come as a single comma-separated string: $oneOf:a,b,c → args = ["a,b,c"]
    const options = args[0].split(',');
    return randomElement(options);
  },
};

/** Every built-in generator name. Copy that counts them (README, docs) must match its length. */
export const BUILTIN_VARIABLE_NAMES: readonly string[] = Object.keys(BUILTIN_VARIABLES);

// --- Request context resolver ---

function resolveRequestVariable(
  path: string,
  context: TemplateContext
): string | undefined {
  const parts = path.split('.');

  // request.method
  if (parts.length === 2 && parts[1] === 'method') {
    return context.request.method;
  }

  // request.path
  if (parts.length === 2 && parts[1] === 'path') {
    return context.request.path;
  }

  // request.body (raw)
  if (parts.length === 2 && parts[1] === 'body') {
    if (context.request.parsedBody) {
      return context.request.parsedBody.raw ?? '';
    }
    return context.request.body ?? '';
  }

  // request.header.<name>
  if (parts.length >= 3 && parts[1] === 'header') {
    const headerName = parts.slice(2).join('.');
    // Case-insensitive header lookup
    const lowerName = headerName.toLowerCase();
    for (const [key, value] of Object.entries(context.request.headers)) {
      if (key.toLowerCase() === lowerName) {
        return value;
      }
    }
    return '';
  }

  // request.query.<name>
  if (parts.length >= 3 && parts[1] === 'query') {
    const queryName = parts.slice(2).join('.');
    return context.request.query[queryName] ?? '';
  }

  // request.body.<field.path> (resolve from ParsedBody.fields when available)
  if (parts.length >= 3 && parts[1] === 'body') {
    if (context.request.parsedBody) {
      const fieldKey = parts.slice(2).join('.');
      const value = context.request.parsedBody.fields[fieldKey];
      return value !== undefined ? value : '';
    }
    // Fallback: ad-hoc parsing when ParsedBody is not available
    const fieldPath = parts.slice(2);
    const contentType = getContentType(context.request.headers);
    if (contentType === 'application/x-www-form-urlencoded') {
      return resolveFormField(context.request.body, fieldPath[0]);
    }
    return resolveJsonPath(context.request.body, fieldPath);
  }

  return undefined;
}

function resolveJsonPath(
  body: string | null,
  fieldPath: string[]
): string {
  if (!body) return '';

  try {
    let current: unknown = JSON.parse(body);
    for (const key of fieldPath) {
      if (current === null || current === undefined) return '';
      if (typeof current !== 'object') return '';
      current = (current as Record<string, unknown>)[key];
    }
    if (current === null || current === undefined) return '';
    if (typeof current === 'object') return JSON.stringify(current);
    return String(current);
  } catch {
    return '';
  }
}

// --- Content-type helpers ---

function getContentType(headers: Record<string, string>): string {
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === 'content-type') {
      // Strip charset and other parameters (e.g., "application/x-www-form-urlencoded; charset=utf-8")
      return value.split(';')[0].trim().toLowerCase();
    }
  }
  return '';
}

function resolveFormField(body: string | null, fieldName: string): string {
  if (!body) return '';
  try {
    const params = new URLSearchParams(body);
    return params.get(fieldName) ?? '';
  } catch {
    return '';
  }
}

/**
 * Parse a URL-encoded body string into a key-value record.
 * Exported for use in UI components.
 */
export function parseFormBody(body: string): Record<string, string> {
  const result: Record<string, string> = {};
  try {
    const params = new URLSearchParams(body);
    params.forEach((value, key) => {
      result[key] = value;
    });
  } catch {
    // not valid form data
  }
  return result;
}

/**
 * Check if a content-type string indicates URL-encoded form data.
 */
export function isFormUrlEncoded(contentType: string): boolean {
  return contentType.split(';')[0].trim().toLowerCase() === 'application/x-www-form-urlencoded';
}

/**
 * Extract the content-type from a headers object (case-insensitive).
 */
export function getContentTypeFromHeaders(headers: Record<string, string>): string {
  return getContentType(headers);
}

// --- Main template processor ---

// Matches {{...}} patterns, capturing the variable name inside
const TEMPLATE_REGEX = /\{\{([^}]+)\}\}/g;

function resolveVariable(
  variableName: string,
  context: TemplateContext
): string | undefined {
  const name = variableName.trim();

  // 1. Built-in generators ($-prefixed)
  if (name.startsWith('$')) {
    // Exact match first (non-parameterized)
    const resolver = BUILTIN_VARIABLES[name];
    if (resolver) return resolver();

    // Parameterized: $generatorName:arg1:arg2
    const colonIdx = name.indexOf(':');
    if (colonIdx !== -1) {
      const baseName = name.slice(0, colonIdx);
      const args = name.slice(colonIdx + 1).split(':');
      const paramResolver = BUILTIN_VARIABLES[baseName];
      if (paramResolver) return paramResolver(args);
    }

    return undefined;
  }

  // 2. Request context (request.*)
  if (name.startsWith('request.')) {
    return resolveRequestVariable(name, context);
  }

  // 3. Variables (vars.*)
  if (name.startsWith('vars.') && context.variables) {
    const varName = name.slice(5); // strip 'vars.'
    const value = context.variables[varName];
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  // 3b. Project variables (project.*)
  if (name.startsWith('project.') && context.project) {
    const varName = name.slice(8);
    const value = context.project[varName];
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  // 3c. Global variables (globals.*)
  if (name.startsWith('globals.') && context.globals) {
    const varName = name.slice(8); // strip 'globals.'
    const value = context.globals[varName];
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  // 4. Path parameters (backward compatible)
  if (name in context.pathParams) {
    return context.pathParams[name];
  }

  // Unknown variable - leave as-is
  return undefined;
}

// Matches innermost repeat blocks only (body must not contain nested {{#repeat:}})
const REPEAT_REGEX = /\{\{#repeat:(\d+)\}\}((?:(?!\{\{#repeat:)[\s\S])*?)\{\{\/repeat\}\}/g;
const MAX_REPEAT = 500;

/**
 * Expand {{#repeat:N}}...{{/repeat}} blocks into repeated content joined by commas.
 * Processes innermost blocks first to support nesting. Caps N at 500.
 * Replaces {{$index}} with the 0-based iteration number within each block.
 */
export function expandRepeatBlocks(template: string): string {
  let result = template;
  // Loop until no more repeat blocks remain (handles nesting inside-out)
  while (REPEAT_REGEX.test(result)) {
    REPEAT_REGEX.lastIndex = 0;
    result = result.replace(REPEAT_REGEX, (_match, countStr: string, body: string) => {
      const count = Math.min(parseInt(countStr, 10), MAX_REPEAT);
      const parts: string[] = [];
      for (let i = 0; i < count; i++) {
        parts.push(body.replace(/\{\{\$index\}\}/g, String(i)));
      }
      return parts.join(',');
    });
  }
  return result;
}

/**
 * Process all template variables in a response body string.
 *
 * Replaces {{variable}} placeholders with their resolved values.
 * Unknown variables are left unchanged.
 */
export function processTemplate(
  template: string,
  context: TemplateContext
): string {
  const expanded = expandRepeatBlocks(template);
  return expanded.replace(TEMPLATE_REGEX, (match, variableName: string) => {
    const resolved = resolveVariable(variableName, context);
    return resolved !== undefined ? resolved : match;
  });
}

/**
 * Strip template variables from a string for JSON validation purposes.
 *
 * Replaces {{...}} tokens with context-appropriate placeholders so that
 * JSON.parse can validate the surrounding structure even when template
 * variables are present.
 */
export function stripTemplatesForValidation(str: string): string {
  let result = '';
  let inString = false;
  let i = 0;

  while (i < str.length) {
    // Handle string boundaries (skip escaped quotes)
    if (str[i] === '"' && (i === 0 || str[i - 1] !== '\\')) {
      inString = !inString;
      result += str[i];
      i++;
      continue;
    }

    // Handle template tokens
    if (str[i] === '{' && str[i + 1] === '{') {
      const end = str.indexOf('}}', i + 2);
      if (end !== -1) {
        if (inString) {
          // Inside a JSON string: replace with plain text placeholder
          result += '__tpl__';
        } else {
          // Outside a JSON string: replace with valid JSON value
          result += '"__tpl__"';
        }
        i = end + 2;
        continue;
      }
    }

    result += str[i];
    i++;
  }

  return result;
}

/**
 * Process template variables in response header values.
 *
 * Takes a headers object and replaces {{variable}} placeholders
 * in each header value with their resolved values.
 */
export function processHeaders(
  headers: Record<string, string>,
  context: TemplateContext
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    result[key] = processTemplate(value, context);
  }
  return result;
}

/** Common content types for mock API responses */
export const COMMON_CONTENT_TYPES = [
  'application/json',
  'text/plain',
  'text/html',
  'application/xml',
  'text/csv',
] as const;

const BINARY_CONTENT_TYPES = ['application/pdf', 'application/octet-stream', 'application/zip'];
const BINARY_CONTENT_TYPE_FAMILIES = ['image/', 'audio/', 'video/', 'font/'];

/**
 * Whether a content type names a binary format. Response bodies are sent as
 * UTF-8 text, so a body of this type would reach the caller corrupted.
 */
export function isBinaryContentType(contentType: string): boolean {
  const type = contentType.split(';')[0].trim().toLowerCase();
  if (type === 'image/svg+xml') return false;
  return (
    BINARY_CONTENT_TYPES.includes(type) ||
    BINARY_CONTENT_TYPE_FAMILIES.some((family) => type.startsWith(family))
  );
}
